import ReportsDoughnutChart from "../Charts/ReportsDoughnutChart.vue";
import ThinBarChart from "../Charts/ThinBarChart.vue";
import SupportTracker from '../Charts/SupportTracker.vue';
import HelperMixin from '../../Shared/HelperMixin.vue';
import ApexCharts from 'apexcharts';

export default {
    name: 'home',
    mixins: [HelperMixin],

    components: {
        ReportsDoughnutChart,
        ThinBarChart,
        SupportTracker,
    },

    async created() {
        this.loginDetails = await this.CheckLoginStatus();

        if (this.loginDetails) {
            if (!this.requireUserType([1, 2, 3])) return; else {
                this.loadDashboardData();
            }
        } else {
            this.logout();
        }

        window.scrollTo(0, 0);
    },

    data() {
        return {
            loading: true,
            error: null,
            dashboard: null,
            loginDetails: null,
            activeTab: 'regular',
            // Chart instances for cleanup
            charts: {
                users: null,
                subscriptions: null,
                revenueForecast: null,
                annualProfit: null,
                totalIncome: null,
                weeklySchedules: null,
                monthlyTripTypes: null
            },
            // RTL Chart defaults with Arabic locale
            rtlChartDefaults: {
                chart: {
                    fontFamily: 'inherit',
                    foreColor: '#adb0bb',
                    defaultLocale: 'ar',
                    locales: [{
                        name: 'ar',
                        options: {
                            months: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
                                     'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
                            shortMonths: ['ينا', 'فبر', 'مار', 'أبر', 'ماي', 'يون',
                                          'يول', 'أغس', 'سبت', 'أكت', 'نوف', 'ديس'],
                            days: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
                            shortDays: ['أحد', 'اثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت']
                        }
                    }]
                },
                tooltip: { 
                    theme: 'dark',
                    style: { fontSize: '12px' } 
                },
                yaxis: { opposite: true },
                legend: { position: 'bottom' }
            },
            arabicMonths: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
                          'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
            arabicDays: ['السبت', 'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة']
        };
    },

    // A chart drawn over an all-zero series looks identical to a chart that failed
    // to load — empty axes and nothing else. These flags let the template say
    // "no data for this period" instead of leaving the operator guessing.
    computed: {
        hasWeeklyTripData() {
            const d = this.dashboard;
            if (!d) return false;
            return (d.weeklyCompletedTrips || []).some(n => n > 0)
                || (d.weeklyCancelledTrips || []).some(n => n > 0);
        },
        hasUserTrendData() {
            const d = this.dashboard;
            if (!d) return false;
            return (d.last9DaysPassengers || []).some(x => x.count > 0)
                || (d.last9DaysDrivers || []).some(x => x.count > 0);
        },
        hasRevenueData() {
            const d = this.dashboard;
            if (!d) return false;
            return (d.currentYearMonthlyRevenue || []).some(n => n > 0)
                || (d.previousYearMonthlyRevenue || []).some(n => n > 0);
        },
        hasTripTypeData() {
            const d = this.dashboard;
            if (!d) return false;
            return (d.monthlyTripsByType || []).some(m => m.regular > 0 || m.shared > 0 || m.schools > 0);
        },
    },

    beforeDestroy() {
        // Clean up chart instances
        Object.values(this.charts).forEach(chart => {
            if (chart) {
                chart.destroy();
            }
        });
    },

    methods: {

        // Format helpers
        // For month-over-month deltas, where the +/- sign carries meaning.
        formatPercent(value) {
            if (value === null || value === undefined) return '0%';
            const sign = value >= 0 ? '+' : '';
            return sign + value.toFixed(1) + '%';
        },

        // For standalone rates (e.g. completion rate). A leading '+' here reads as
        // "went up by 76.9%" when it actually means "76.9% of trips completed".
        formatRate(value) {
            if (value === null || value === undefined) return '0%';
            return value.toFixed(1) + '%';
        },

        formatNumber(value) {
            if (value === null || value === undefined) return '0';
            return value.toLocaleString('en-US');
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return '0 د.ل';
            return value.toLocaleString('en-US') + ' د.ل';
        },

        formatDate(value) {
            if (!value) return '-';
            const date = new Date(value);
            return date.toLocaleDateString('ar-LY', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        // Some legacy rows were written through a non-Unicode client, so their Arabic
        // text landed in the DB as literal '?' bytes ("????? ??????? - ??????").
        // Rendering that verbatim looks like a font/encoding bug in the dashboard.
        // Show an honest placeholder instead of garbage.
        formatAddress(value) {
            if (!value) return '-';
            const text = String(value).trim();
            const questionMarks = (text.match(/\?/g) || []).length;
            const letters = (text.match(/[^\s\-،,]/g) || []).length;
            if (letters > 0 && questionMarks / letters > 0.5) return 'عنوان غير متوفر';
            return text;
        },

        formatTripLevel(level) {
            const levels = {
                'Pending': 'قيد الانتظار',
                'Accepted': 'مقبولة',
                'Driver Arrived': 'وصل السائق',
                'In Progress': 'جارية',
                'Completed': 'مكتملة',
                'Cancelled': 'ملغاة'
            };
            return levels[level] || level || '-';
        },

        // ✅ moved from inline <script>
        handleColorTheme(theme) {
            document.documentElement.setAttribute('data-color-theme', theme);
        },

        async loadDashboardData() {
            this.loading = true;
            this.error = null;
            try {
                const response = await this.$http.GetDashboardOverview();
                this.dashboard = response.data;
            } catch (e) {
                this.error = 'حدث خطأ في تحميل البيانات';
                console.error('Dashboard API Error:', e);
            } finally {
                this.loading = false;
            }

            // The chart containers live inside `v-else-if="dashboard"`, which is only
            // in the DOM once `loading` is false. Initialising from inside the try
            // block queued initCharts() while the spinner was still rendered, so every
            // document.querySelector('#chart-id') returned null and all seven charts
            // silently no-op'd (`if (!el) return`) — the page looked like it had no data.
            // Wait for the post-`loading=false` render to commit before drawing.
            if (this.dashboard) {
                await this.$nextTick();
                this.initCharts();
            }
        },

        initCharts() {
            if (!this.dashboard) return;

            // Initialize all charts
            this.initUsersChart();
            this.initSubscriptionsChart();
            this.initRevenueForecastChart();
            this.initAnnualProfitChart();
            this.initTotalIncomeChart();
            this.initWeeklySchedulesChart();
            this.initMonthlyTripTypesChart();
        },

        // Line Chart for last 9 days passengers and drivers (Requirement 3.3)
        initUsersChart() {
            const el = document.querySelector('#users');
            if (!el || !this.dashboard) return;

            // Prepare data from last9DaysPassengers and last9DaysDrivers
            const passengersData = this.dashboard.last9DaysPassengers?.map(d => d.count) || [];
            const driversData = this.dashboard.last9DaysDrivers?.map(d => d.count) || [];
            const categories = this.dashboard.last9DaysPassengers?.map(d => {
                const date = new Date(d.date);
                return date.toLocaleDateString('ar-LY', { day: 'numeric', month: 'short' });
            }) || [];

            const options = {
                chart: {
                    id: 'users-chart',
                    type: 'line',
                    height: 91,
                    sparkline: { enabled: true },
                    group: 'sparklines',
                    ...this.rtlChartDefaults.chart
                },
                series: [
                    {
                        name: 'الركاب الجدد',
                        color: 'var(--bs-primary)',
                        data: passengersData
                    },
                    {
                        name: 'السائقين الجدد',
                        color: 'var(--bs-secondary)',
                        data: driversData
                    }
                ],
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 0,
                        inverseColors: false,
                        opacityFrom: 0.2,
                        opacityTo: 0.1,
                        stops: [100]
                    }
                },
                markers: { size: 0 },
                tooltip: {
                    ...this.rtlChartDefaults.tooltip,
                    fixed: { enabled: true, position: 'right' },
                    x: { show: false }
                },
                xaxis: {
                    categories: categories
                },
                yaxis: { opposite: true }
            };

            if (this.charts.users) {
                this.charts.users.destroy();
            }
            this.charts.users = new ApexCharts(el, options);
            this.charts.users.render();
        },

        // Sparkline Bar Chart for subscriptions (Requirement 10.3)
        initSubscriptionsChart() {
            const el = document.querySelector('#subscriptions');
            if (!el || !this.dashboard) return;

            // Get last 6 months subscriptions data
            const subscriptionsData = this.dashboard.last6MonthsSubscriptions?.map(m => m.count) || [0, 0, 0, 0, 0, 0];
            const maxValue = Math.max(...subscriptionsData, 1);
            const backgroundData = subscriptionsData.map(() => maxValue);

            const options = {
                series: [
                    {
                        name: 'الاشتراكات',
                        data: subscriptionsData
                    },
                    {
                        name: 'الخلفية',
                        data: backgroundData
                    }
                ],
                chart: {
                    type: 'bar',
                    height: 98,
                    stacked: true,
                    offsetX: -10,
                    toolbar: { show: false },
                    sparkline: { enabled: true },
                    ...this.rtlChartDefaults.chart
                },
                colors: ['var(--bs-white)', 'rgba(255,255,255,0.5)'],
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '26%',
                        borderRadius: 3,
                        borderRadiusApplication: 'end',
                        borderRadiusWhenStacked: 'all'
                    }
                },
                dataLabels: { enabled: false },
                tooltip: this.rtlChartDefaults.tooltip,
                legend: { show: false },
                yaxis: { opposite: true }
            };

            if (this.charts.subscriptions) {
                this.charts.subscriptions.destroy();
            }
            this.charts.subscriptions = new ApexCharts(el, options);
            this.charts.subscriptions.render();
        },

        // Line Chart for revenue forecast - current year vs previous year (Requirements 11.1, 11.2)
        initRevenueForecastChart() {
            const el = document.querySelector('#revenue-forecast');
            if (!el || !this.dashboard) return;

            const currentYear = new Date().getFullYear();
            const previousYear = currentYear - 1;

            // Get monthly revenue data
            const currentYearData = this.dashboard.currentYearMonthlyRevenue || Array(12).fill(0);
            const previousYearData = this.dashboard.previousYearMonthlyRevenue || Array(12).fill(0);

            const options = {
                series: [
                    {
                        name: currentYear.toString(),
                        data: currentYearData
                    },
                    {
                        name: previousYear.toString(),
                        data: previousYearData.map(v => -v) // Negative for visual comparison
                    }
                ],
                chart: {
                    toolbar: { show: false },
                    type: 'bar',
                    height: 295,
                    stacked: true,
                    offsetX: -15,
                    ...this.rtlChartDefaults.chart
                },
                colors: ['var(--bs-primary)', 'var(--bs-danger)'],
                plotOptions: {
                    bar: {
                        horizontal: false,
                        barHeight: '60%',
                        columnWidth: '15%',
                        borderRadius: 6,
                        borderRadiusApplication: 'end',
                        borderRadiusWhenStacked: 'all'
                    }
                },
                dataLabels: { enabled: false },
                legend: { show: false },
                grid: {
                    show: true,
                    padding: { top: 0, bottom: 0, right: 0 },
                    borderColor: 'rgba(0,0,0,0.05)',
                    xaxis: { lines: { show: true } },
                    yaxis: { lines: { show: true } }
                },
                xaxis: {
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    categories: this.arabicMonths,
                    labels: {
                        style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' }
                    }
                },
                yaxis: {
                    opposite: true,
                    tickAmount: 4,
                    labels: {
                        formatter: (val) => {
                            return Math.abs(val).toLocaleString('en-US');
                        }
                    }
                },
                tooltip: {
                    ...this.rtlChartDefaults.tooltip,
                    y: {
                        formatter: (val) => {
                            return Math.abs(val).toLocaleString('en-US') + ' د.ل';
                        }
                    }
                }
            };

            if (this.charts.revenueForecast) {
                this.charts.revenueForecast.destroy();
            }
            this.charts.revenueForecast = new ApexCharts(el, options);
            this.charts.revenueForecast.render();
        },

        // Sparkline Area Chart for annual profit (Requirement 12.2)
        initAnnualProfitChart() {
            const el = document.querySelector('#annual-profit');
            if (!el || !this.dashboard) return;

            // Get last 6 months revenue data
            const revenueData = this.dashboard.last6MonthsRevenue || [0, 0, 0, 0, 0, 0];

            const options = {
                chart: {
                    id: 'annual-profit',
                    type: 'area',
                    height: 80,
                    sparkline: { enabled: true },
                    group: 'sparklines',
                    ...this.rtlChartDefaults.chart
                },
                series: [
                    {
                        name: 'الإيرادات',
                        color: 'var(--bs-primary)',
                        data: revenueData
                    }
                ],
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                fill: {
                    type: 'gradient',
                    color: 'var(--bs-primary)',
                    gradient: {
                        shadeIntensity: 0,
                        inverseColors: false,
                        opacityFrom: 0.1,
                        opacityTo: 0.1,
                        stops: [100]
                    }
                },
                markers: { size: 0 },
                tooltip: {
                    ...this.rtlChartDefaults.tooltip,
                    fixed: { enabled: true, position: 'right' },
                    x: { show: false },
                    y: {
                        formatter: (val) => val.toLocaleString('en-US') + ' د.ل'
                    }
                },
                yaxis: { opposite: true }
            };

            if (this.charts.annualProfit) {
                this.charts.annualProfit.destroy();
            }
            this.charts.annualProfit = new ApexCharts(el, options);
            this.charts.annualProfit.render();
        },

        // Sparkline Line Chart for total daily income (Requirement 14.3)
        initTotalIncomeChart() {
            const el = document.querySelector('#total-income');
            if (!el || !this.dashboard) return;

            // Get last 7 days income data
            const incomeData = this.dashboard.last7DaysIncome || [0, 0, 0, 0, 0, 0, 0];

            const options = {
                chart: {
                    id: 'total-income',
                    type: 'line',
                    height: 60,
                    sparkline: { enabled: true },
                    group: 'sparklines',
                    ...this.rtlChartDefaults.chart
                },
                series: [
                    {
                        name: 'الدخل',
                        color: 'var(--bs-danger)',
                        data: incomeData
                    }
                ],
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                markers: { size: 0 },
                tooltip: {
                    ...this.rtlChartDefaults.tooltip,
                    fixed: { enabled: true, position: 'right' },
                    x: { show: false },
                    y: {
                        formatter: (val) => val.toLocaleString('en-US') + ' د.ل'
                    }
                },
                yaxis: { opposite: true }
            };

            if (this.charts.totalIncome) {
                this.charts.totalIncome.destroy();
            }
            this.charts.totalIncome = new ApexCharts(el, options);
            this.charts.totalIncome.render();
        },

        // Stacked Bar Chart for weekly schedules - completed vs cancelled trips (Requirement 15)
        initWeeklySchedulesChart() {
            const el = document.querySelector('#weekly-schedules');
            if (!el || !this.dashboard) return;

            // Get weekly trips data (Saturday to Friday)
            const completedTrips = this.dashboard.weeklyCompletedTrips || [0, 0, 0, 0, 0, 0, 0];
            const cancelledTrips = this.dashboard.weeklyCancelledTrips || [0, 0, 0, 0, 0, 0, 0];

            const options = {
                series: [
                    {
                        name: 'مكتملة',
                        data: completedTrips
                    },
                    {
                        name: 'ملغاة',
                        data: cancelledTrips
                    }
                ],
                chart: {
                    type: 'bar',
                    height: 300,
                    stacked: true,
                    toolbar: { show: false },
                    ...this.rtlChartDefaults.chart
                },
                colors: ['var(--bs-primary)', 'var(--bs-danger)'],
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '40%',
                        borderRadius: 6,
                        borderRadiusApplication: 'end',
                        borderRadiusWhenStacked: 'all'
                    }
                },
                dataLabels: { enabled: false },
                legend: {
                    position: 'bottom',
                    horizontalAlign: 'center'
                },
                grid: {
                    show: true,
                    borderColor: 'rgba(0,0,0,0.05)',
                    xaxis: { lines: { show: false } },
                    yaxis: { lines: { show: true } }
                },
                xaxis: {
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    categories: this.arabicDays, // السبت إلى الجمعة
                    labels: {
                        style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' }
                    }
                },
                yaxis: {
                    opposite: true,
                    labels: {
                        style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' }
                    }
                },
                tooltip: {
                    ...this.rtlChartDefaults.tooltip,
                    y: {
                        formatter: (val) => val + ' رحلة'
                    }
                }
            };

            if (this.charts.weeklySchedules) {
                this.charts.weeklySchedules.destroy();
            }
            this.charts.weeklySchedules = new ApexCharts(el, options);
            this.charts.weeklySchedules.render();
        },

        // Bar Chart for monthly trip types (Requirement 4)
        initMonthlyTripTypesChart() {
            const el = document.querySelector('#monthly-trip-types');
            if (!el || !this.dashboard) return;

            // Get monthly trips by type data
            const monthlyData = this.dashboard.monthlyTripsByType || [];
            
            // Extract data for each trip type
            const regularData = monthlyData.map(m => m.regular || 0);
            const sharedData = monthlyData.map(m => m.shared || 0);
            const schoolsData = monthlyData.map(m => m.schools || 0);

            const options = {
                series: [
                    {
                        name: 'عادية',
                        data: regularData
                    },
                    {
                        name: 'مشتركة',
                        data: sharedData
                    },
                    {
                        name: 'مدارس',
                        data: schoolsData
                    }
                ],
                chart: {
                    type: 'bar',
                    height: 350,
                    stacked: false,
                    toolbar: { show: false },
                    ...this.rtlChartDefaults.chart
                },
                colors: ['#5D87FF', '#49BEFF', '#FFAE1F'],
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '50%',
                        borderRadius: 4
                    }
                },
                dataLabels: { enabled: false },
                legend: {
                    position: 'bottom',
                    horizontalAlign: 'center'
                },
                grid: {
                    show: true,
                    borderColor: 'rgba(0,0,0,0.05)',
                    xaxis: { lines: { show: false } },
                    yaxis: { lines: { show: true } }
                },
                xaxis: {
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    categories: this.arabicMonths,
                    labels: {
                        style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' }
                    }
                },
                yaxis: {
                    opposite: true,
                    labels: {
                        style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' }
                    }
                },
                tooltip: {
                    ...this.rtlChartDefaults.tooltip,
                    y: {
                        formatter: (val) => val + ' رحلة'
                    }
                }
            };

            if (this.charts.monthlyTripTypes) {
                this.charts.monthlyTripTypes.destroy();
            }
            this.charts.monthlyTripTypes = new ApexCharts(el, options);
            this.charts.monthlyTripTypes.render();
        }
    }
};
